import { create } from 'zustand';
import {
  resolutionApi,
  type ClusterSummaryData,
  type EdgeRelationCount,
  type ResolutionBatchSummary,
  type ResolutionResultData,
} from '@/services/api';

interface ClusterExplorerFilters {
  entity_type: string;
  client_id: string;
  similarity_threshold: number;
}

interface BatchesFilters {
  entity_type: string;
  limit: number;
}

interface ResolutionPipelineState {
  // Cluster Explorer (session-only; clusters are not persisted in Phase 3)
  clusterFilters: ClusterExplorerFilters;
  clusters: ClusterSummaryData[];
  selectedCluster: ClusterSummaryData | null;
  clusterLoading: boolean;
  clusterError: string | null;
  resolvingEntityType: boolean;
  resolveStatus: string | null;

  // Batches
  batchesFilters: BatchesFilters;
  batches: ResolutionBatchSummary[];
  batchesLoading: boolean;
  selectedBatch: ResolutionBatchSummary | null;
  batchResults: ResolutionResultData[];
  batchResultsLoading: boolean;
  batchResultDecisionFilter: string;

  // Edge Quality
  edgeCounts: EdgeRelationCount[];
  edgesLoading: boolean;
  retypeBusy: boolean;
  retypeStatus: string | null;

  // actions
  setClusterFilters: (partial: Partial<ClusterExplorerFilters>) => void;
  runClusterFind: () => Promise<void>;
  selectCluster: (c: ClusterSummaryData | null) => void;
  runResolutionForCurrentFilters: () => Promise<void>;

  setBatchesFilters: (partial: Partial<BatchesFilters>) => void;
  fetchBatches: () => Promise<void>;
  selectBatch: (b: ResolutionBatchSummary | null) => Promise<void>;
  setBatchResultDecisionFilter: (decision: string) => Promise<void>;

  fetchEdgeCounts: () => Promise<void>;
  retypeGenericEdges: () => Promise<void>;
}

export const useResolutionPipelineStore = create<ResolutionPipelineState>(
  (set, get) => ({
    clusterFilters: {
      entity_type: 'market_concept',
      client_id: '',
      similarity_threshold: 0.85,
    },
    clusters: [],
    selectedCluster: null,
    clusterLoading: false,
    clusterError: null,
    resolvingEntityType: false,
    resolveStatus: null,

    batchesFilters: { entity_type: '', limit: 25 },
    batches: [],
    batchesLoading: false,
    selectedBatch: null,
    batchResults: [],
    batchResultsLoading: false,
    batchResultDecisionFilter: '',

    edgeCounts: [],
    edgesLoading: false,
    retypeBusy: false,
    retypeStatus: null,

    setClusterFilters: (partial) =>
      set((s) => ({ clusterFilters: { ...s.clusterFilters, ...partial } })),

    runClusterFind: async () => {
      const { clusterFilters } = get();
      if (!clusterFilters.entity_type) {
        set({ clusterError: 'Pick an entity type first' });
        return;
      }
      set({ clusterLoading: true, clusterError: null, selectedCluster: null });
      try {
        const res = await resolutionApi.findClusters({
          entity_type: clusterFilters.entity_type,
          client_id: clusterFilters.client_id || undefined,
          similarity_threshold: clusterFilters.similarity_threshold,
        });
        set({ clusters: res.clusters, clusterLoading: false });
      } catch (err) {
        set({
          clusterError: (err as Error).message,
          clusterLoading: false,
          clusters: [],
        });
      }
    },

    selectCluster: (c) => set({ selectedCluster: c }),

    runResolutionForCurrentFilters: async () => {
      const { clusterFilters } = get();
      if (!clusterFilters.entity_type) return;
      set({ resolvingEntityType: true, resolveStatus: null });
      try {
        const res = await resolutionApi.runResolution({
          entity_type: clusterFilters.entity_type,
          client_id: clusterFilters.client_id || undefined,
          similarity_threshold: clusterFilters.similarity_threshold,
        });
        set({
          resolveStatus: `Resolved batch ${res.batch_id}: ${res.merges_performed} merges across ${res.clusters_found} clusters.`,
        });
        // Refresh the batches tab in the background so the new batch shows up.
        get().fetchBatches();
      } catch (err) {
        set({ resolveStatus: `Resolve failed: ${(err as Error).message}` });
      } finally {
        set({ resolvingEntityType: false });
      }
    },

    setBatchesFilters: (partial) => {
      set((s) => ({ batchesFilters: { ...s.batchesFilters, ...partial } }));
      get().fetchBatches();
    },

    fetchBatches: async () => {
      const { batchesFilters } = get();
      set({ batchesLoading: true });
      try {
        const res = await resolutionApi.listBatches({
          entity_type: batchesFilters.entity_type || undefined,
          limit: batchesFilters.limit,
        });
        set({ batches: res.batches, batchesLoading: false });
      } catch {
        set({ batches: [], batchesLoading: false });
      }
    },

    selectBatch: async (b) => {
      set({ selectedBatch: b, batchResults: [], batchResultDecisionFilter: '' });
      if (!b) return;
      set({ batchResultsLoading: true });
      try {
        const res = await resolutionApi.listResults({
          batch_id: b.batch_id === '_unbatched' ? undefined : b.batch_id,
          limit: 100,
          order_by: 'created_desc',
        });
        set({ batchResults: res.results, batchResultsLoading: false });
      } catch {
        set({ batchResults: [], batchResultsLoading: false });
      }
    },

    setBatchResultDecisionFilter: async (decision) => {
      const { selectedBatch } = get();
      set({ batchResultDecisionFilter: decision });
      if (!selectedBatch) return;
      set({ batchResultsLoading: true });
      try {
        const res = await resolutionApi.listResults({
          batch_id:
            selectedBatch.batch_id === '_unbatched'
              ? undefined
              : selectedBatch.batch_id,
          decision: decision || undefined,
          limit: 100,
          order_by: 'created_desc',
        });
        set({ batchResults: res.results, batchResultsLoading: false });
      } catch {
        set({ batchResults: [], batchResultsLoading: false });
      }
    },

    fetchEdgeCounts: async () => {
      set({ edgesLoading: true });
      try {
        const res = await resolutionApi.getEdgeRelationCounts(100);
        set({ edgeCounts: res.counts, edgesLoading: false });
      } catch {
        set({ edgeCounts: [], edgesLoading: false });
      }
    },

    retypeGenericEdges: async () => {
      set({ retypeBusy: true, retypeStatus: null });
      try {
        const res = await resolutionApi.retypeGenericEdges();
        set({
          retypeStatus: res.message || 'Retype started',
          retypeBusy: false,
        });
        // Refresh counts a couple seconds later so the UI reflects the job effect.
        setTimeout(() => get().fetchEdgeCounts(), 4000);
      } catch (err) {
        set({
          retypeStatus: `Retype failed: ${(err as Error).message}`,
          retypeBusy: false,
        });
      }
    },
  }),
);
